import { useState, type FormEvent } from 'react';
import { signUpWithEmail, signInWithGoogle, authErrorMessage } from '@/lib/auth';

interface SignUpFormProps {
  onSuccess: () => void;
  onSwitchMode: (mode: 'signin') => void;
}

export default function SignUpForm({ onSuccess, onSwitchMode }: SignUpFormProps) {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setBusy(true);
    try {
      await signUpWithEmail(email.trim(), password, displayName);
      onSuccess();
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
      onSuccess();
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <label className="auth-field">
        <span className="auth-label">Name</span>
        <input
          type="text"
          autoComplete="name"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={busy}
          placeholder="First Last"
        />
      </label>
      <label className="auth-field">
        <span className="auth-label">Email</span>
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        />
      </label>
      <label className="auth-field">
        <span className="auth-label">Password</span>
        <input
          type="password"
          autoComplete="new-password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
          placeholder="6 chars or more"
        />
      </label>

      {error && <div className="auth-error">{error}</div>}

      <button type="submit" className="btn block" disabled={busy}>
        {busy ? 'Creating…' : 'Create Account →'}
      </button>
      <button type="button" className="btn dark block" onClick={handleGoogle} disabled={busy}>
        Continue with Google
      </button>

      <div className="auth-links">
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Already have an account?</span>
        <button type="button" className="auth-link" onClick={() => onSwitchMode('signin')}>
          Sign in →
        </button>
      </div>
    </form>
  );
}
